/**
 * Centralized Type-Safe Modal URL Configuration
 *
 * This module provides a single source of truth for all modal-related URL configurations,
 * including modal IDs, entity parameters, and URL construction utilities.
 *
 * Features:
 * - Type-safe modal ID to entity parameter mappings
 * - Automatic URL construction with validation
 * - Support for both singular and plural parameter names
 * - Integration with global modal stack
 * - Full TypeScript inference
 *
 * @example
 * ```tsx
 * // Open a deal detail modal
 * const url = buildModalUrl('deals-detail', { dealsId: '123' });
 * // Result: "/current-path?modal=deals-detail&dealsId=123"
 *
 * // Get configuration for a modal
 * const config = getModalConfig('contacts-detail');
 * // Result: { modalId: 'contacts-detail', entityName: 'contacts', ... }
 * ```
 */

import type { ModalConfig } from "../modal-url-config/types";
import { contactModalsModalUrlConfig } from "../modal-url-config/contact-modals";
import { companyModalsModalUrlConfig } from "../modal-url-config/company-modals";
import { dealModalsModalUrlConfig } from "../modal-url-config/deal-modals";
import { taskModalsModalUrlConfig } from "../modal-url-config/task-modals";
import { ticketModalsModalUrlConfig } from "../modal-url-config/ticket-modals";
import { timeEntryModalsModalUrlConfig } from "../modal-url-config/time-entry-modals";
import { communicationModalsModalUrlConfig } from "../modal-url-config/communication-modals";
import { employeeModalsModalUrlConfig } from "../modal-url-config/employee-modals";
import { recurringTransactionModalsModalUrlConfig } from "../modal-url-config/recurring-transaction-modals";
import { contractModalsModalUrlConfig } from "../modal-url-config/contract-modals";
import { socialMediaPostModalsModalUrlConfig } from "../modal-url-config/social-media-post-modals";
import { emailCampaignModalsModalUrlConfig } from "../modal-url-config/email-campaign-modals";
import { adminUserModalsModalUrlConfig } from "../modal-url-config/admin-user-modals";
import { offeringModalsModalUrlConfig } from "../modal-url-config/offering-modals";
import { recurringChargeModalsModalUrlConfig } from "../modal-url-config/recurring-charge-modals";

export type { ModalConfig } from "../modal-url-config/types";

export const MODAL_URL_CONFIG = {
  ...contactModalsModalUrlConfig,
  ...companyModalsModalUrlConfig,
  ...dealModalsModalUrlConfig,
  ...taskModalsModalUrlConfig,
  ...ticketModalsModalUrlConfig,
  ...timeEntryModalsModalUrlConfig,
  ...communicationModalsModalUrlConfig,
  ...employeeModalsModalUrlConfig,
  ...recurringTransactionModalsModalUrlConfig,
  ...contractModalsModalUrlConfig,
  ...socialMediaPostModalsModalUrlConfig,
  ...emailCampaignModalsModalUrlConfig,
  ...adminUserModalsModalUrlConfig,
  ...offeringModalsModalUrlConfig,
  ...recurringChargeModalsModalUrlConfig,
} as const satisfies Record<string, ModalConfig>;

/**
 * Type-safe modal IDs from the configuration
 */
export type ConfiguredModalId = keyof typeof MODAL_URL_CONFIG;

export interface ParsedHubModalUrlState {
  modalId: ConfiguredModalId;
  entityId: string | null;
  params: Record<string, string>;
}

/**
 * Extract entity parameter names for a modal
 */
export type ModalParams<T extends ConfiguredModalId> = {
  [K in
    | (typeof MODAL_URL_CONFIG)[T]["singularParam"]
    | (typeof MODAL_URL_CONFIG)[T]["pluralParam"]]?: string;
};

/**
 * Get modal configuration by modal ID
 * Throws an error if the modal is not configured
 */
export function getModalConfig<T extends ConfiguredModalId>(
  modalId: T
): (typeof MODAL_URL_CONFIG)[T];
export function getModalConfig(modalId: string): ModalConfig;
export function getModalConfig(modalId: string): ModalConfig {
  const config = MODAL_URL_CONFIG[modalId as ConfiguredModalId];

  if (!config) {
    throw new Error(
      `Modal "${modalId}" is not configured. Add it to MODAL_URL_CONFIG in modal-url-config.ts`
    );
  }

  return config;
}

/**
 * Check if a modal ID is configured
 */
export function isModalConfigured(modalId: string): modalId is ConfiguredModalId {
  return modalId in MODAL_URL_CONFIG;
}

/**
 * Hub URL sync should only run for modal IDs registered in MODAL_URL_CONFIG.
 */
export function isModalUrlEnabled(modalId: string): modalId is ConfiguredModalId {
  return isModalConfigured(modalId);
}

/**
 * Get all configured modal IDs
 */
export function getConfiguredModalIds(): ConfiguredModalId[] {
  return Object.keys(MODAL_URL_CONFIG) as ConfiguredModalId[];
}

/**
 * Parse Hub modal URL state for configured modals only.
 * Returns null for missing/unknown modal IDs.
 */
export function parseHubModalUrlState(
  searchParams: URLSearchParams | null | undefined
): ParsedHubModalUrlState | null {
  if (!searchParams) return null;

  const modalParam = searchParams.get("modal");
  if (!modalParam || !isModalConfigured(modalParam)) {
    return null;
  }

  return {
    modalId: modalParam,
    entityId: extractEntityId(modalParam, searchParams),
    params: extractModalParams(modalParam, searchParams),
  };
}

/**
 * Get modal configuration by entity name
 */
export function getModalsByEntity(entityName: string): ModalConfig[] {
  return Object.values(MODAL_URL_CONFIG).filter((config) => config.entityName === entityName);
}

/**
 * Extract entity ID from URL parameters for a given modal
 * Checks both singular and plural parameter names
 */
export function extractEntityId(
  modalId: string,
  searchParams: URLSearchParams | null | undefined
): string | null {
  if (!searchParams) return null;

  if (!isModalConfigured(modalId)) {
    return null;
  }

  const config = getModalConfig(modalId);

  // Check both singular and plural forms
  const singularValue = searchParams.get(config.singularParam);
  const pluralValue = searchParams.get(config.pluralParam);

  return singularValue || pluralValue || null;
}

/**
 * Extract all entity parameters from URL for a modal
 */
export function extractModalParams(
  modalId: string,
  searchParams: URLSearchParams | null | undefined
): Record<string, string> {
  if (!searchParams || !isModalConfigured(modalId)) {
    return {};
  }

  const config = getModalConfig(modalId);
  const params: Record<string, string> = {};

  // Extract entity ID parameters
  const entityId = extractEntityId(modalId, searchParams);
  if (entityId) {
    params[config.pluralParam] = entityId;
  }

  // Extract additional parameters if configured
  const additionalParams = (config as ModalConfig).additionalParams;
  if (additionalParams && Array.isArray(additionalParams)) {
    for (const param of additionalParams) {
      const value = searchParams.get(param);
      if (value) {
        params[param] = value;
      }
    }
  }

  return params;
}

/**
 * Remove entity ID parameters from URL
 * Removes both singular and plural parameter names
 */
export function removeEntityIdParams(
  modalId: string,
  searchParams: URLSearchParams
): URLSearchParams {
  if (!isModalConfigured(modalId)) {
    return new URLSearchParams(searchParams);
  }

  const config = getModalConfig(modalId);
  const newParams = new URLSearchParams(searchParams);

  // Remove both singular and plural forms
  newParams.delete(config.singularParam);
  newParams.delete(config.pluralParam);

  return newParams;
}

/**
 * Build a clean URL without modal and entity parameters
 */
export function buildCleanUrl(
  pathname: string,
  searchParams: URLSearchParams | null | undefined,
  modalId: string
): string {
  const params = new URLSearchParams(searchParams?.toString() || "");

  // Remove modal parameter
  params.delete("modal");

  // Remove entity ID parameters if modal is configured
  const cleanParams = isModalConfigured(modalId) ? removeEntityIdParams(modalId, params) : params;

  return `${pathname}${cleanParams.toString() ? `?${cleanParams.toString()}` : ""}`;
}
